import {
  leaderboardContract,
  playerContract,
  prizeContract,
  promoContract,
  subscriptionContract,
  TErrorResponse,
  TTicketDetails,
  winnerContract,
} from '@daymade/api-contracts';
import { Monthly10k } from '@daymade/common-lib/constants';
import { SubscriptionStatus } from '@daymade/common-lib/dbEnums';
import { throwError } from '@daymade/common-lib/errors';
import { oneOfOrUndefined } from '@daymade/common-lib/types';
import { ClientInferResponseBody } from '@ts-rest/core';
import {
  k_UPGRADE_POPUP_ACTIVE,
  k_UPGRADE_POPUP_LAST_DISMISSED,
  k_UPGRADE_POPUP_TIMER_START_KEY,
} from 'components/UpgradePopup/UpgradePopup';
import {
  DaymadeDrawTypePlanCode,
  MainDrawScheduleCode,
  MainDrawTypeCode,
} from 'constants/Draw';
import mixpanel from 'mixpanel-browser';
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { RecurringSubscriptionDiscount } from 'types/RecurringSubscriptionDiscount';
import { User } from 'types/User';
import {
  identifyUser,
  optInTracking,
  trackSignOutUser,
} from 'utils/analytics/analytics';
import { tryApi } from 'utils/api/common';
import { submitQuestion } from 'utils/api/feedback';
import {
  authApiClient,
  drawApiClient,
  drawOrderApiClient,
  leaderboardApiClient,
  playerApiClient,
  prizeApiClient,
  promoApiClient,
  subscriptionApiClient,
  tokenApiClient,
  winnerApiClient,
} from 'utils/apiClient';
import { inviteUrl } from 'utils/common';
import { getCookie, removeCookie, setCookie } from 'utils/cookie';
import HttpStatusCode from 'utils/HttpStatusCode';
import { convertImageToBlob } from 'utils/image/convertImageToBlob';
import { RootStore } from './RootStore';

export type TSubscriptions = ClientInferResponseBody<
  typeof subscriptionContract.auth.getPlayerSubscriptions,
  200
>;

type TPlayerDetails = ClientInferResponseBody<
  typeof playerContract.auth.getDetails,
  200
>;

type TWishlistPrize = ClientInferResponseBody<
  typeof prizeContract.auth.getFavouritedPrizes,
  200
>[number];

type TLeaderboardStats = ClientInferResponseBody<
  typeof leaderboardContract.auth.getRankAndPointsTotal,
  200
>;

type TUnclaimedPrizeWin = ClientInferResponseBody<
  typeof winnerContract.auth.getUnclaimedPrizeWins,
  200
>[number];

type TClaimedPrizeWin = ClientInferResponseBody<
  typeof winnerContract.auth.getClaimedPrizeWins,
  200
>[number];

type TPromoPrizeRewards = ClientInferResponseBody<
  typeof promoContract.auth.getPromoPrizeRewardsOfPlayer,
  200
>;

export type UserHydration = {
  currentUser?: TPlayerDetails['player'] | null;
  balances?: TPlayerDetails['balances'] | null;
  isFtmp: boolean;
  currentGuestUser?: User | null;
  wishListPrizes?: TWishlistPrize[] | null;
};

export class UserStore {
  root: RootStore;
  initialized: boolean = false;
  currentUser: TPlayerDetails['player'] | null = null;
  balances: TPlayerDetails['balances'] | null = null;
  leaderboardStats: TLeaderboardStats | null = null;
  isFtmp: boolean = true;
  currentGuestUser: User | null = null;
  eligibleForUpgrade: boolean = true;
  recurringSubscriptionDiscount: RecurringSubscriptionDiscount = {
    rewardCode: '',
    rewardValuePerStep: 0,
    discountedWeeksRemaining: 0,
  };
  wishListPrizes: TWishlistPrize[] = [];
  unclaimedPrizeWins: TUnclaimedPrizeWin[] | 'LOADING' | 'ERROR' = 'LOADING';
  claimedPrizeWins: TClaimedPrizeWin[] | 'LOADING' | 'ERROR' = 'LOADING';
  promoPrizeRewards: TPromoPrizeRewards | 'LOADING' | 'ERROR' = 'LOADING';

  impactShareOptions: null | Record<string, { href: string }> = null;

  outsideUK: boolean = false;

  subscriptions: TSubscriptions = [];
  activeTickets: TTicketDetails[] = [];
  upcomingSubscriptionDrawDate: Date | null = null;

  constructor(root: RootStore) {
    this.root = root;
    this.initializeCurrentUser();

    makeObservable(this, {
      root: true,
      balances: true,
      isFtmp: true,
      isAdmin: computed,
      wishListPrizes: true,
      updateTokenBalance: true,
      setImpactShareOptions: true,
      updateCurrentUser: true,
      setBalances: true,
      loadLeaderboardStats: true,
      setWishlistPrizes: true,
      fetchWishListPrizes: true,
      setCurrentUser: action,
      setCurrentGuestUser: action,
      logout: action,
      initializeCurrentUser: action,
      setTokenBalance: action,
      setTreesPlanted: action,
      setLeaderboardStats: action,
      fetchSubscriptions: action,
      fetchActiveTickets: action,
      fetchUpcomingDrawDate: action,
      hydrate: action,
      setAvatarIcon: action,
      toggleWishlistPrize: action,
      submitHelpQuestion: action,
      submitAccountDetails: action,
      setIsFtmp: action,
      getPrizeWinsAndRewards: action,

      checkCountry: action,
      initialized: observable,
      impactShareOptions: observable,
      currentUser: observable,
      currentGuestUser: observable,
      leaderboardStats: observable,
      eligibleForUpgrade: observable,
      recurringSubscriptionDiscount: observable,
      unclaimedPrizeWins: observable,
      claimedPrizeWins: observable,
      promoPrizeRewards: observable,
      unclaimedPromoPrizeRewards: computed,
      claimedPromoPrizeRewards: computed,
      claimablePrizesCount: computed,
      outsideUK: observable,
      isInMonthlyRaffle: computed,

      favouritedPrizeIds: computed,
      subscriptions: observable,
      activeSubscriptions: computed,
      inactiveSubscriptions: computed,
      hasFreemiumSubscription: computed,
      hasLiteSubscription: computed,
      activeTickets: observable,
      isOperator: computed,
      subscriptionStatus: computed,
      subscriptionStatusInfo: computed,
      eligibleForIntroOffer: computed,
      canUpgradeSubscription: computed,
      subscriptionsCount: computed,
      upcomingSubscriptionDrawDate: observable,

      activeWeeklyDrawTickets: computed,
      activeWeeklyDrawSubscriptionTickets: computed,
      activeTokenTownTickets: computed,
    });

    this.checkCountry();
  }

  checkCountry = async () => {
    const resp = await authApiClient.public.country();
    if (resp.status === HttpStatusCode.OK) {
      this.outsideUK =
        resp.body.country !== 'unknown' && resp.body.country !== 'GB';
    }
  };

  initializeCurrentUser = async () => {
    if (typeof window === 'undefined') {
      return;
    }
    await this.updateCurrentUser();
    await this.fetchWishListPrizes();

    if (!this.currentUser) {
      // const guestJwt = getCookie('guest_jwt');
      // this.setCurrentGuestUser(await getMe(guestJwt)); TODO Guest User
    }

    this.setImpactShareOptions();
    this.initialized = true;
  };

  updateTokenBalance = async () => {
    const result = await tokenApiClient.auth.getTokenBalanceForUser();

    switch (result.status) {
      case HttpStatusCode.OK:
        this.setTokenBalance(result.body);
        break;
      case HttpStatusCode.BAD_REQUEST:
        this.root.UIStore.displayToast({
          title: result.body.message,
          color: 'red',
          timeout: 3000,
        });
        break;
      case HttpStatusCode.UNAUTHORIZED:
        this.root.UIStore.displayToast({
          title: result.body.message,
          color: 'red',
          timeout: 3000,
        });
        break;
      default:
        this.root.UIStore.displayToast({
          title: 'Error: Cannot fetch token balance. Please try again later.',
          color: 'red',
          timeout: 3000,
        });
        break;
    }
  };

  setImpactShareOptions = () => {
    if (!this.currentUser) return;

    const referralLink = inviteUrl(this.currentUser.referralId.toString());
    const impactText = `I've planted ${this.currentUser.treesPlanted === 1 ? '1 tree' : `${this.currentUser.treesPlanted?.toLocaleString()} trees`} with DAYMADE!`;

    this.impactShareOptions = {
      facebook: {
        href: `https://www.facebook.com/dialog/share?app_id=256975402191022&display=popup&href=${encodeURIComponent(referralLink)}&quote=${impactText}`,
      },
      whatsapp: {
        href: `whatsapp://send?text=${encodeURIComponent(`${impactText} ${referralLink}`)}`,
      },
      email: {
        href: `mailto:?subject=${encodeURIComponent(
          'Check Out DAYMADE!',
        )}&body=${encodeURIComponent(`${impactText} ${referralLink}`)}`,
      },
      linkedin: {
        href: `https://www.linkedin.com/shareArticle?mini=true&title=${encodeURIComponent(impactText)}&url=${encodeURIComponent(referralLink)}`,
      },
    };
  };

  updateCurrentUser = async (token?: string) => {
    token = token ?? getCookie('jwt');

    if (!this.currentUser && !token) return;

    setCookie('jwt', token, { secure: true, expires: 30 });

    const result = await playerApiClient.auth.getDetails();

    if ([400, 401, 404].includes(result.status)) {
      const { message } = result.body as TErrorResponse; // Type assertion using the inferred type

      this.root.UIStore.displayToast({
        title: `Oops, something went wrong. ${message}`,
        color: '#FF6600',
        timeout: 9000,
      });
      return;
    }

    // if(result.status === 500){
    //   const { message } = result.body as TErrorResponse; // Type assertion using the inferred type

    //   this.root.UIStore.displayToast({
    //     title: `Oops, something went wrong: ${message}`,
    //     color: '#FF6600',
    //     timeout: 9000,
    //   });
    //   return;
    // }

    if (result.status === HttpStatusCode.OK) {
      const { player, balances, isFtmp } = result.body;
      this.setCurrentUser(player);
      this.setBalances(balances);
      this.setIsFtmp(isFtmp);
    }

    // TODO add guest user check
    return this.currentUser;
  };

  setCurrentUser = (user: TPlayerDetails['player'] | null) => {
    const userChanged = this.currentUser?.id !== user?.id;

    this.currentUser = user?.id ? user : null;

    if (user) {
      optInTracking();
      if (typeof window === 'undefined') {
        return;
      }

      mixpanel.register({
        name: user.firstName + ' ' + user.lastName,
        email: user.emailAddress,
      });

      identifyUser({
        emailAddress: user?.emailAddress,
        id: user?.id as any,
        firstName: user?.firstName,
        lastName: user?.lastName,
      });

      this.fetchSubscriptions();
      this.fetchActiveTickets();
      this.getPrizeWinsAndRewards();
      this.fetchUpcomingDrawDate();
    }

    if (userChanged) {
      if (user) {
        this.fetchWishListPrizes();
        this.loadLeaderboardStats();
      }
    }
  };

  getPrizeWinsAndRewards = async () => {
    this.unclaimedPrizeWins = 'LOADING';
    this.claimedPrizeWins = 'LOADING';
    this.promoPrizeRewards = 'LOADING';

    const [unclaimedPrizesResp, claimedPrizesResp, promoPrizesResp] =
      await Promise.all([
        tryApi(winnerApiClient.auth.getUnclaimedPrizeWins()),
        tryApi(winnerApiClient.auth.getClaimedPrizeWins()),
        tryApi(promoApiClient.auth.getPromoPrizeRewardsOfPlayer()),
      ]);

    if (unclaimedPrizesResp.status === HttpStatusCode.OK) {
      this.unclaimedPrizeWins = unclaimedPrizesResp.body;
    } else {
      this.unclaimedPrizeWins = 'ERROR';
    }

    if (claimedPrizesResp.status === HttpStatusCode.OK) {
      this.claimedPrizeWins = claimedPrizesResp.body;
    } else {
      this.claimedPrizeWins = 'ERROR';
    }

    if (promoPrizesResp.status === HttpStatusCode.OK) {
      this.promoPrizeRewards = promoPrizesResp.body;
    } else {
      this.promoPrizeRewards = 'ERROR';
    }

    // if any of the requests errorred, we must rethrow it, so that it is reported to Sentry and the global error handler
    if (unclaimedPrizesResp.status === 'error') {
      throwError('Failed to load unclaimed prizes', {
        cause: unclaimedPrizesResp.error,
      });
    }
    if (claimedPrizesResp.status === 'error') {
      throwError('Failed to load claimed prizes', {
        cause: claimedPrizesResp.error,
      });
    }
    if (promoPrizesResp.status === 'error') {
      throwError('Failed to load promo prizes', {
        cause: promoPrizesResp.error,
      });
    }
  };

  get unclaimedPromoPrizeRewards() {
    return this.promoPrizeRewards === 'LOADING' ||
      this.promoPrizeRewards === 'ERROR'
      ? this.promoPrizeRewards
      : this.promoPrizeRewards.unclaimed;
  }

  get claimedPromoPrizeRewards() {
    return this.promoPrizeRewards === 'LOADING' ||
      this.promoPrizeRewards === 'ERROR'
      ? this.promoPrizeRewards
      : this.promoPrizeRewards.claimed;
  }

  get claimablePrizesCount() {
    const now = new Date();
    return (
      (Array.isArray(this.unclaimedPrizeWins)
        ? this.unclaimedPrizeWins.filter(
            (prize) => prize.expiresAt && prize.expiresAt > now,
          ).length
        : 0) +
      (Array.isArray(this.unclaimedPromoPrizeRewards)
        ? this.unclaimedPromoPrizeRewards.filter(
            (reward) => reward.expiresAt && reward.expiresAt > now,
          ).length
        : 0)
    );
  }

  setBalances = (balances: TPlayerDetails['balances'] | null) => {
    this.balances = balances;
  };

  setIsFtmp = (isFtmp: boolean) => {
    this.isFtmp = isFtmp;
  };

  setCurrentGuestUser = (user: User) => {
    this.currentGuestUser = user;
  };

  setTokenBalance = (balance: number) => {
    if (!this.balances) {
      return;
    }
    this.balances.tokenBalance = balance;
  };

  setTreesPlanted = (treesPlanted: number) => {
    if (!this.currentUser) {
      return;
    }
    this.currentUser.treesPlanted = treesPlanted;
  };

  loadLeaderboardStats = async () => {
    const resp = await leaderboardApiClient.auth.getRankAndPointsTotal();

    if (resp.status === HttpStatusCode.OK) {
      this.setLeaderboardStats(resp.body);
    }
  };

  setLeaderboardStats = (stats: TLeaderboardStats | null) => {
    this.leaderboardStats = stats;
  };

  setAvatarIcon = async (fileObject: any) => {
    if (!this.currentUser) {
      return;
    }

    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(fileObject.type)) {
      this.root.UIStore.displayToast({
        title:
          'Error: Unsupported File Type. Please upload only image files in one of the following formats: JPEG or PNG.',
        color: 'red',
        timeout: 5000,
      });
      return;
    }

    const file = convertImageToBlob(fileObject);

    if (!file) return;

    const result = await playerApiClient.auth.uploadAvatarIcon({
      body: {
        image: file,
      },
    });

    if (result.status === HttpStatusCode.OK) {
      this.root.UIStore.displayToast({
        title: 'Avatar icon updated!',
        color: 'green',
        timeout: 3000,
      });
    } else {
      this.root.UIStore.displayToast({
        title: result.body.message,
        color: 'red',
        timeout: 3000,
      });
    }
  };

  submitAccountDetails = async (
    file: any,
    leaderboardName: string,
    removedPicture: boolean,
    avatarIconLink?: string,
  ) => {
    if (!this.currentUser) {
      return;
    }

    if (leaderboardName.localeCompare(this.currentUser?.friendlyName ?? '')) {
      const usernameChangeResult = await playerApiClient.auth.updateDetails({
        body: { player: { friendlyName: leaderboardName } },
      });

      if (usernameChangeResult.status !== HttpStatusCode.OK) {
        this.root.UIStore.displayToast({
          title:
            'message' in usernameChangeResult.body
              ? usernameChangeResult.body.message
              : 'An unknown error occurred',
          color: 'red',
          timeout: 3000,
        });
        return;
      }
    }

    if (file) {
      await this.setAvatarIcon(file);
    } else if (avatarIconLink) {
      const result = await playerApiClient.auth.updateDetails({
        body: { player: { avatarLocation: avatarIconLink } },
      });

      if (result.status === HttpStatusCode.OK) {
        this.root.UIStore.displayToast({
          title: 'Avatar icon updated!',
          color: 'green',
          timeout: 3000,
        });
      } else {
        this.root.UIStore.displayToast({
          title:
            'message' in result.body
              ? result.body.message
              : 'An unknown error occurred',
          color: 'red',
          timeout: 3000,
        });
      }
    }

    this.updateCurrentUser();
  };

  setWishlistPrizes = action((wishlistPrizes: TWishlistPrize[]) => {
    localStorage.removeItem('wishListPrizes');
    this.wishListPrizes = wishlistPrizes;
  });

  fetchWishListPrizes = async () => {
    if (this.currentUser === null) return;

    const response = await prizeApiClient.auth.getFavouritedPrizesV2({
      query: {
        drawTypeCode: MainDrawTypeCode,
        drawScheduleCode: MainDrawScheduleCode.TUESDAY,
      },
    });

    if (response.status !== HttpStatusCode.OK) {
      this.setWishlistPrizes([]);
    } else {
      this.setWishlistPrizes(
        response.body.map((prize) => ({
          ...prize,
          prizeInstanceId: prize.id,
        })),
      );
    }
  };

  toggleWishlistPrize = async (prizeInstanceId: number, toggle: boolean) => {
    const result = toggle
      ? await prizeApiClient.auth.favouritePrize({
          body: {
            prizeId: prizeInstanceId,
          },
        })
      : await prizeApiClient.auth.unfavouritePrize({
          body: {
            prizeId: prizeInstanceId,
          },
        });

    if (result.status === HttpStatusCode.OK) {
      this.root.UIStore.displayToast({
        title: toggle
          ? 'Added prize to wishlist'
          : 'Removed prize from wishlist',
        color: 'green',
        timeout: 3000,
      });

      if (!toggle) {
        // If the item is being removed, update the local list
        runInAction(() => {
          this.setWishlistPrizes(
            this.wishListPrizes.filter((prize) => prize.id !== prizeInstanceId),
          );
        });
      }

      // Invalidate cache by removing the localStorage entry
      localStorage.removeItem('wishListPrizes');
    } else {
      this.root.UIStore.displayToast({
        title: toggle
          ? 'Unable to add prize to wishlist. Please try again later.'
          : 'Unable to remove prize from wishlist. Please try again later.',
        color: 'red',
        timeout: 3000,
      });
    }
  };

  get isInMonthlyRaffle() {
    const isInMonthlyRaffle: boolean =
      this.currentUser?.abtestVariants?.[Monthly10k.abTestName] ===
      Monthly10k.abTestValue;
    return isInMonthlyRaffle;
  }

  get subscriptionsCount() {
    return this.subscriptions.length;
  }

  get subscriptionStatus() {
    return (
      this.activeSubscriptions?.[0]?.drawSchedulePlans[0]?.drawTypePlan?.code ??
      undefined
    );
  }

  /** Returns information about the current users subscription status as a strongly-typed object */
  get subscriptionStatusInfo() {
    const stonglyTypedSubscriptionStatus = oneOfOrUndefined(
      this.subscriptionStatus,
      'PREMIUM',
      'LITE',
      'FREEMIUM',
    );

    return {
      subscriberStatus: stonglyTypedSubscriptionStatus,
      isFreemiumSubscriber: stonglyTypedSubscriptionStatus === 'FREEMIUM',
      isPremiumSubscriber: stonglyTypedSubscriptionStatus === 'PREMIUM',
      isPayingSubscriber:
        stonglyTypedSubscriptionStatus === 'PREMIUM' ||
        stonglyTypedSubscriptionStatus === 'LITE',
    };
  }

  get activeSubscriptions() {
    return this.subscriptions.filter((subscription) => {
      return [
        SubscriptionStatus.ACTIVE,
        SubscriptionStatus.SNOOZED,
        SubscriptionStatus.FAILING,
      ].includes(subscription.subscription.status);
    });
  }

  get inactiveSubscriptions() {
    return this.subscriptions.filter(
      (subscription) =>
        ![
          SubscriptionStatus.ACTIVE,
          SubscriptionStatus.SNOOZED,
          SubscriptionStatus.FAILING,
        ].includes(subscription.subscription.status),
    );
  }

  get canUpgradeSubscription() {
    return this.hasFreemiumSubscription || this.hasLiteSubscription;
  }

  get hasFreemiumSubscription() {
    return (
      this.activeSubscriptions[0]?.drawSchedulePlans[0].drawTypePlan.code ===
      DaymadeDrawTypePlanCode.FREE
    );
  }

  get hasLiteSubscription() {
    return (
      this.activeSubscriptions[0]?.drawSchedulePlans[0].drawTypePlan.code ===
      DaymadeDrawTypePlanCode.LITE
    );
  }

  get isOperator() {
    return (
      this.currentUser?.role === 'OPERATOR' ||
      this.currentUser?.role === 'ADMIN'
    );
  }

  get isAdmin() {
    return this.currentUser?.role === 'ADMIN';
  }

  get activeWeeklyDrawTickets() {
    return this.activeTickets.filter(
      (ticket) => ticket.drawType.group === 'DAYMADEDRAW',
    );
  }

  get activeWeeklyDrawSubscriptionTickets() {
    return this.activeTickets.filter(
      (ticket) =>
        ticket.drawType.group === 'DAYMADEDRAW' &&
        ticket.subscription &&
        [
          SubscriptionStatus.ACTIVE,
          SubscriptionStatus.SNOOZED,
          SubscriptionStatus.FAILING,
        ].includes(ticket.subscription?.status),
    );
  }

  get activeTokenTownTickets() {
    return this.activeTickets.filter(
      (ticket) => ticket.drawType.group === 'MINIGAMES',
    );
  }

  get favouritedPrizeIds() {
    return new Set(this.wishListPrizes.map((prize) => prize.id));
  }

  get eligibleForIntroOffer() {
    return this.currentUser ? this.isFtmp : true;
  }

  fetchSubscriptions = async () => {
    const res = await subscriptionApiClient.auth.getPlayerSubscriptions();

    if (res.status === HttpStatusCode.OK) {
      this.subscriptions = res.body;
    } else {
      this.subscriptions = [];
    }
  };

  fetchActiveTickets = async () => {
    const res = await drawOrderApiClient.auth.getPlayerTickets({
      query: {
        excludeCompleted: 'true',
        excludeDrawTypeCodes: ['LEADERBOARD'],
      },
    });

    if (res.status === HttpStatusCode.OK) {
      this.activeTickets = res.body;
    }
  };

  fetchUpcomingDrawDate = async () => {
    const res = await drawApiClient.auth.getPlayersUpcomingDrawDate();

    if (res.status === HttpStatusCode.OK) {
      this.upcomingSubscriptionDrawDate = res.body.upcomingDrawDate;
    }
  };

  submitHelpQuestion = async (question: string) => {
    if (!this.currentUser) return;
    const res = await submitQuestion(this.currentUser.emailAddress, question);
    if (res.successful === true) {
      this.root.UIStore.displayToast({
        title:
          "Thanks for your question! We'll get back to you using your email address.",
        color: 'green',
        timeout: 3000,
      });
      return;
    } else {
      this.root.UIStore.displayToast({
        title: 'Error: Cannot send question. Please try again later.',
        color: 'red',
        timeout: 3000,
      });
    }
  };

  logout = () => {
    removeCookie('jwt');
    removeCookie('referral_id');
    removeCookie('promo_code');
    localStorage.removeItem('promo_code');
    localStorage.removeItem('promoCode');
    localStorage.removeItem(k_UPGRADE_POPUP_TIMER_START_KEY);
    localStorage.removeItem(k_UPGRADE_POPUP_ACTIVE);
    localStorage.removeItem(k_UPGRADE_POPUP_LAST_DISMISSED);

    let hasNavigated = false;
    const navigateToLogoutTarget = () => {
      if (!hasNavigated) {
        hasNavigated = true;
        // Forcefully navigate to the home page, with a logged out query string parameter, so that we show the logged out
        // toast. This ensures that all stores are completely empty since it will reload the homepage
        window.location.href = '/?loggedout=true';
      }
    };

    // Track the sign out event, and pass a callback so we do the logout once the event has tracked
    trackSignOutUser(navigateToLogoutTarget);
    // Set up a timeout fallback in case callback doesn't fire quickly enough
    setTimeout(navigateToLogoutTarget, 1000);
  };

  hydrate(data?: UserHydration) {
    if (data?.currentUser && data.currentUser.id) {
      this.currentUser = data.currentUser;
    }
    if (data?.currentGuestUser) {
      this.currentGuestUser = data.currentGuestUser;
    }

    if (data?.wishListPrizes) {
      this.wishListPrizes = data.wishListPrizes;
    }

    this.initialized = true;
  }
}
